import { useMemo } from "react"
import { HorairesOuvertureDepartmentEnum, WeekDayEnum, WeekDayLabel, WorkingHour } from "./type"
import { SelectOption } from "../../types/SelectOption"
import { Timezone } from "../FuseauHoraire/type"
import moment from "moment-timezone"

type Props = {
    workingHours: WorkingHour[]
    isDealershipSettings: boolean
    openEditModal: (workingHoursToEdit: WorkingHour) => void
    onDelete: (workingHourId: number) => void
    onDuplicate: (workingHourToDuplicate: WorkingHour) => void
    dealerships?: SelectOption[]
    timezones?: Timezone[]
}

WorkingHoursTable.defaultProps = {
    isDealershipSettings: false
}

export function WorkingHoursTable(props: Props) {
    /**
     * Jours de la semaine dans l'ordre
     */
    const weekDays = [
        WeekDayEnum.Monday,
        WeekDayEnum.Tuesday,
        WeekDayEnum.Wednesday,
        WeekDayEnum.Thursday,
        WeekDayEnum.Friday,
        WeekDayEnum.Saturday,
        WeekDayEnum.Sunday,
    ]

    /**
     * Récupère le nom du département à partir de sa valeur numérique
     */
    const getDepartmentLabel = (department: HorairesOuvertureDepartmentEnum): string => {
        return Object.entries(HorairesOuvertureDepartmentEnum)
            .find(([key, value]) => value === department)?.[0] || String(department)
    }

    /**
     * Récupère le nom du site à partir de son ID
     */
    const getDealershipLabel = (siteId: number): string => {
        if (!props.dealerships) return String(siteId)
        const dealership = props.dealerships.find(d => d.value === siteId)
        return dealership?.label || String(siteId)
    }

    /**
     * Formate les créneaux d'un jour spécifique
     * @param workingHour - L'objet WorkingHour
     * @param day - Le jour de la semaine
     * @returns JSX avec les créneaux ou badge "Fermé"
     */
    const getDaySchedules = (workingHour: WorkingHour, day: WeekDayEnum): JSX.Element => {
        const slots = workingHour.schedules.businessHours[day]

        if (!slots || slots.length === 0) {
            return (
                <span className="badge badge-secondary tw-text-xs">Fermé</span>
            )
        }

        return (
            <div className="tw-flex tw-flex-col tw-gap-1">
                {slots.map((slot, index) => (
                    <span key={index} className="tw-text-xs tw-whitespace-nowrap">
                        {slot[0]} - {slot[1]}
                    </span>
                ))}
            </div>
        )
    }

    const getDepartmentBadge = (workingHour: WorkingHour): string => {
        const baseClasses = "tw-px-3 tw-py-1 tw-rounded tw-text-white tw-font-semibold"
        const colorClass = workingHour.department === HorairesOuvertureDepartmentEnum.APV
            ? "tw-bg-green-500"
            : "tw-bg-blue-500"

        return `${baseClasses} ${colorClass}`
    }

    const getTimezone = (siteId: number | null): string => {
        let timezone = props.timezones?.find((timezone) => timezone.siteId === siteId)?.timezone
        if (!timezone) {
            timezone = props.timezones?.find((timezone) => timezone.siteId === null)?.timezone ?? ""
        }

        if (!timezone) return ""

        const offset = moment.tz(timezone).format('Z')
        return `${timezone} (UTC${offset})`
    }

    /**
     * Obtient l'abréviation du jour (3 premières lettres)
     */
    const getShortDayLabel = (day: WeekDayEnum): string => {
        return WeekDayLabel[day].substring(0, 3)
    }

    /**
     * Trie les horaires d'ouverture par ordre alphabétique des sites
     */
    const sortedWorkingHours = useMemo(() => {
        return [...props.workingHours].sort((a, b) => {
            const labelA = a.dealershipIds?.[0] ? getDealershipLabel(a.dealershipIds[0]) : ''
            const labelB = b.dealershipIds?.[0] ? getDealershipLabel(b.dealershipIds[0]) : ''
            return labelA.localeCompare(labelB)
        })
    }, [props.workingHours, props.dealerships])

    return (
        <>
            <table className="table table-striped table-hover table-sm">
                <thead>
                    <tr>
                        {props.isDealershipSettings && <th>Site</th>}
                        <th>Département</th>
                        <th>Fuseau Horaire</th>
                        {weekDays.map((day) => (
                            <th key={day} className="text-center">
                                {getShortDayLabel(day)}
                            </th>
                        ))}
                        <th className="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedWorkingHours.map((workingHour) => {
                        return (
                            <tr key={workingHour.id}>
                                {props.isDealershipSettings && (
                                    <td className="tw-text-sm">{workingHour.dealershipIds?.[0] ? getDealershipLabel(workingHour.dealershipIds[0]) : '-'}</td>
                                )}
                                <td className="tw-text-sm">
                                    <div className="tw-flex tw-items-center tw-h-full">
                                        <span className={getDepartmentBadge(workingHour)}>{getDepartmentLabel(workingHour.department)}</span>
                                    </div>
                                </td>
                                <td className="tw-text-sm">{ getTimezone(workingHour.dealershipIds?.[0] ?? null) }</td>
                                {weekDays.map((day) => (
                                    <td key={day} className="text-center">
                                        {getDaySchedules(workingHour, day)}
                                    </td>
                                ))}
                                <td className="text-center">
                                    <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-center">
                                        <button
                                            className="tw-text-sm tw-text-blue-500 tw-border-0 tw-bg-blue-500 tw-rounded tw-px-2 tw-py-1"
                                            onClick={() => props.openEditModal(workingHour)}
                                            title="Modifier"
                                        >
                                            <i className="fa fa-edit tw-text-white"></i>
                                        </button>
                                        {props.isDealershipSettings && (
                                            <button
                                                className="tw-text-sm tw-border-0 tw-bg-green-500 tw-rounded tw-px-2 tw-py-1"
                                                onClick={() => props.onDuplicate(workingHour)}
                                                title="Dupliquer"
                                            >
                                                <i className="fa fa-copy tw-text-white"></i>
                                            </button>
                                        )}
                                        <button
                                            className="tw-text-sm tw-border-0 tw-bg-red-500 tw-rounded tw-px-2 tw-py-1"
                                            onClick={() => workingHour.id && props.onDelete(workingHour.id)}
                                            title="Supprimer"
                                        >
                                            <i className="fa fa-trash tw-text-white"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </>
    )
}
